import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconEdit,
  IconFileImport,
  IconPlus,
  IconSearch,
  IconToolsKitchen2,
  IconTrash,
} from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useDeleteItem, useItems } from './itemsApi';
import { ItemFormDrawer } from './ItemFormDrawer';
import { BulkImportModal } from './BulkImportModal';
import { useCategories } from '../categories/categoriesApi';
import type { ItemDto } from './types';

const currencyFmt = new Intl.NumberFormat('en-US', {
  // Tenant currency is ILS for the dev tenant; once we wire useMe() we can
  // pull the user's tenant currency dynamically. For now fixed to ILS.
  style: 'currency',
  currency: 'ILS',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function ItemsPage() {
  const { t, i18n } = useTranslation();

  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const { data: items, isLoading } = useItems(categoryFilter);
  const { data: categories } = useCategories();
  const deleteMutation = useDeleteItem();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<ItemDto | null>(null);
  const [deleting, setDeleting] = useState<ItemDto | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    (categories ?? []).forEach((c) =>
      map.set(c.categoryId, i18n.language === 'ar' ? c.nameAr : (c.nameEn ?? c.nameAr)),
    );
    return map;
  }, [categories, i18n.language]);

  const filteredItems = useMemo(() => {
    if (!items) return [];
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) =>
        it.sku.toLowerCase().includes(q) ||
        it.nameAr.toLowerCase().includes(q) ||
        (it.nameEn ?? '').toLowerCase().includes(q) ||
        it.barcodes.some((b) => b.barcode.toLowerCase().includes(q)),
    );
  }, [items, search]);

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const openEdit = (it: ItemDto) => {
    setEditing(it);
    setDrawerOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync(deleting.itemId);
      notifications.show({
        color: 'green',
        title: t('common.toasts.deleted'),
        message: deleting.nameAr,
      });
      setDeleting(null);
    } catch {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: t('common.toasts.errorBody'),
      });
    }
  };

  const hasItems = filteredItems.length > 0;
  const hasUnfilteredItems = (items?.length ?? 0) > 0;

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.catalog')}
        title={t('items.page.title')}
        subtitle={t('items.page.subtitle')}
        actions={
          <Group gap="xs">
            <Button
              variant="default"
              leftSection={<IconFileImport size={16} stroke={1.8} />}
              onClick={() => setBulkOpen(true)}
            >
              {t('items.page.import')}
            </Button>
            <Button leftSection={<IconPlus size={16} stroke={1.8} />} onClick={openCreate}>
              {t('items.page.create')}
            </Button>
          </Group>
        }
      />

      {/* Filters */}
      <Paper p="md">
        <Group gap="md" wrap="nowrap">
          <TextInput
            placeholder={t('items.search.placeholder')}
            leftSection={<IconSearch size={16} stroke={1.6} />}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder={t('items.filter.allCategories')}
            data={[
              { value: '', label: t('items.filter.allCategories') },
              ...(categories ?? []).map((c) => ({
                value: c.categoryId,
                label: i18n.language === 'ar' ? c.nameAr : (c.nameEn ?? c.nameAr),
              })),
            ]}
            value={categoryFilter ?? ''}
            onChange={(value) => setCategoryFilter(value || null)}
            clearable
            w={240}
          />
        </Group>
      </Paper>

      <Paper p={hasItems ? 0 : 'lg'}>
        {isLoading ? (
          <Stack align="center" py={48}>
            <Loader size="sm" />
          </Stack>
        ) : !hasUnfilteredItems ? (
          <EmptyState
            icon={<IconToolsKitchen2 size={22} stroke={1.6} />}
            title={t('items.empty.title')}
            description={t('items.empty.body')}
            actionLabel={t('items.page.create')}
            onAction={openCreate}
          />
        ) : !hasItems ? (
          <EmptyState
            icon={<IconSearch size={22} stroke={1.6} />}
            title={t('items.empty.noMatch')}
            description={t('items.empty.noMatchBody')}
          />
        ) : (
          <Table verticalSpacing="md" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('items.columns.name')}</Table.Th>
                <Table.Th>{t('items.columns.sku')}</Table.Th>
                <Table.Th>{t('items.columns.category')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('items.columns.price')}</Table.Th>
                <Table.Th>{t('items.columns.status')}</Table.Th>
                <Table.Th style={{ width: 100, textAlign: 'end' }}>
                  {t('common.actions')}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredItems.map((it) => (
                <Table.Tr key={it.itemId}>
                  <Table.Td>
                    <div>
                      <Text fw={500} c="var(--wheels-ink-strong)">
                        {it.nameAr}
                      </Text>
                      {it.nameEn && (
                        <Text fz="xs" c="var(--wheels-ink-muted)">
                          {it.nameEn}
                        </Text>
                      )}
                      {it.barcodes.length > 0 && (
                        <Text
                          fz="xs"
                          c="var(--wheels-ink-muted)"
                          className="font-numeric font-mono"
                          mt={2}
                        >
                          {it.barcodes.find((b) => b.isPrimary)?.barcode ?? it.barcodes[0]!.barcode}
                        </Text>
                      )}
                    </div>
                  </Table.Td>
                  <Table.Td>
                    <span className="font-numeric font-mono text-xs text-[var(--wheels-ink-muted)]">
                      {it.sku}
                    </span>
                  </Table.Td>
                  <Table.Td c="var(--wheels-ink-muted)">
                    {it.categoryId ? categoryNameById.get(it.categoryId) ?? '—' : '—'}
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'end' }}>
                    <Text fw={500} className="font-numeric" c="var(--wheels-ink-strong)">
                      {currencyFmt.format(it.basePrice)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {it.isActive ? (
                      <Badge color="green" variant="light" radius="sm">
                        {t('common.active')}
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" radius="sm">
                        {t('common.inactive')}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Tooltip label={t('common.edit')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={() => openEdit(it)}
                          aria-label={t('common.edit')}
                        >
                          <IconEdit size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t('common.delete')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => setDeleting(it)}
                          aria-label={t('common.delete')}
                        >
                          <IconTrash size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <ItemFormDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        item={editing}
      />

      <ConfirmDialog
        opened={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={t('items.delete.title')}
        message={t('items.delete.body', { name: deleting?.nameAr ?? '' })}
        loading={deleteMutation.isPending}
      />

      <BulkImportModal opened={bulkOpen} onClose={() => setBulkOpen(false)} />
    </Stack>
  );
}
